import React from 'react';
import {
  withStyles,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@material-ui/core';
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  ErrorOutlineOutlined as UrgentOutlined,
} from '@material-ui/icons';
import styles from './style';
import { DateTimeEditor } from '@dataGrid/components/cellEditors';
import appConstant from '../../../../appConstants';
import { formatMonthDateYear } from '@dataGrid/gridUtilities';
import CustomTextField from '../../Dispatch/RouteExecution/DriverPopUp/common/CustomTextField';
import Select from '../../../common/StyledSelect';
import { isValid as isDateTimeValid, format } from 'date-fns';
import { compose } from 'recompose';
import WithDrawer from '../../Dispatch/Common/Drawer/WithDrawer';
import { postData } from '../../../../api/componentAPIs';
import { RepeatEveryYear } from '../../Common/icons';
import TerminalSelect from './components/TerminalSelect';
import { groupBy } from 'lodash';
import {constants} from "./constants"
import { ALPHA_NUMERIC_REGEX_WITH_SPACING } from '../../Dispatch/RouteExecution/constRegex';
import ToolTip from '../../../common/StyledToolTip';
import EditHolidayConfirmationDialog from './components/EditHolidayConfirmationDialog';
import { sanitizeInput } from '../../../../utilities/dataUtils';

class AddEditHoliday extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      holidayDetails: this.getNewHoliday(),
      holidayDetailsError: {
        holidayName: null,
        holidayDate: null,
        countryApplicable: null,
        terminalApplicable: []
      },
      isUpdateInProgress: false,
      terminalOptions: [],
      showEditConfirmationDialog: false,
    };
  }

  componentDidMount() {
    this.setTerminalOptions();
  }

  getNewHoliday = () => {
    const { holidayDetails } = this.props;
    const countryApplicable = holidayDetails?.countryApplicable?.join(',') || '';
    const terminalApplicable = holidayDetails?.terminalApplicable || [];
    return {
      holidayId: holidayDetails?.holidayId || null,
      holidayName: holidayDetails?.holidayName || null,
      holidayDate: holidayDetails?.holidayDate || null,
      countryApplicable,
      countryApplicableBackupData: holidayDetails?.countryApplicable || [],
      terminalApplicable,
      nonAccessibleTerminals: this.getNonAccessibleTerminals(terminalApplicable),
      shouldRepeatEveryYear: holidayDetails?.shouldRepeatEveryYear || false,
    };
  };

  // Update holiday Details based on inputs
  handleChange = (name) => (value) => {
    if ('countryApplicable' === name) {
      value = value?.map((x) => x.label).join(',') || null;
    }
    this.setState(
      (state) => ({
        holidayDetails: {
          ...state.holidayDetails,
          [name]: value
        },
        holidayDetailsError: {
          ...state.holidayDetailsError,
          [name]: this.getFieldError(name, value)
        }
      }),
      () => {
        if (name !== 'countryApplicable') {
          return;
        }
        this.setTerminalOptions();
      }
    );
  };

  setTerminalOptions = () => {
    this.setState({
      terminalOptions: this.getFormattedTerminalOptions(),
    });
  };

  getFieldError = (field, value) => {
    if (['holidayName', 'holidayDate'].includes(field)) {
      return value?.trim() ? null : '* Required';
    }
    return null;
  };

  // Set Error messages for each field
  getErrorInfo = () => {
    const holidayDetailsError = {};
    Object.keys(this.state.holidayDetails).forEach((fieldName) => {
      holidayDetailsError[fieldName] = this.getFieldError(
        fieldName,
        this.state.holidayDetails[fieldName]
      );
    });
    return holidayDetailsError;
  };

  getDuplicateHolidayError = () => {
    const { holidayName, holidayDate, holidayId } = this.state.holidayDetails;
    const formattedName = (str) => str?.trim().toLowerCase().replace(/\s/g, ''); // converts to lowercase, and removes spaces
    const duplicateHoliday = this.props.holidayList.find(
      (holiday) =>
        holidayId !== holiday.holidayId &&
        holiday.holidayDate === format(holidayDate, appConstant.APP_DATE_FORMAT) &&
        formattedName(holiday.holidayName) === formattedName(holidayName)
    );
    return duplicateHoliday
      ? `Duplicate: ${duplicateHoliday.holidayName} on ${duplicateHoliday.holidayDate} already exists.`
      : null;
  };

  updateHolidayDate = (value) => {
    const valueToBeUpdate = !value? null: value;
    if (this.state.holidayDetails.holidayDate !== value) {
      if (!value || isDateTimeValid(value)) {
        this.handleChange('holidayDate')(valueToBeUpdate);
      }
    }
  };

  saveHolidayData = () => {
    const errorInfo = this.getErrorInfo();
    const duplicateHoliday = this.getDuplicateHolidayError();
    errorInfo.holidayName= errorInfo.holidayName || duplicateHoliday;
    this.setState({
      holidayDetailsError: errorInfo
    });
    const error = Object.values(errorInfo).filter(Boolean).length;
    if (!error) {
      if (this.state.holidayDetails.holidayId) {
        this.setState({
          showEditConfirmationDialog: true
        });
      } else {
        this.saveHoliday()
      }
    }
  };

  onCloseEditConfirmationDialog = () => {
    this.setState({
      showEditConfirmationDialog: false
    });
  };

  getTerminalsByCountryCodes = () => {
    const countryApplicable = this.state.holidayDetails.countryApplicable?.split(',') || [];
    let filteredTerminalList = this.props.userAccessibleTerminals;
    if (countryApplicable.length) {
      filteredTerminalList = filteredTerminalList.filter((terminal) =>
        countryApplicable.includes(terminal.country)
      );
    }
    return filteredTerminalList.map((terminal) => terminal.label);
  };

  getCountryApplicableData = () => {
    if (this.state.holidayDetails.terminalApplicable?.length > 0) {
      return [];
    }
    const userAccessibleCountryList = this.props.countryList.map(country => country.value);
    const selectedTerminals = this.state.holidayDetails.countryApplicable?.split(',') || [];
    const userNonAccessibleTerminals = this.state.holidayDetails.countryApplicableBackupData.filter(
      (country) => !userAccessibleCountryList.includes(country)
    );
    return [...new Set([...userNonAccessibleTerminals, ...selectedTerminals])];
  };

  getNonAccessibleTerminals = (holidayTerminals) => {
    const userAccessibleTerminalList = this.props.userAccessibleTerminals.map(
      (terminal) => terminal.value
    );
    return holidayTerminals.filter((terminal) => !userAccessibleTerminalList.includes(terminal));
  };

  getTerminalApplicableData = () => {
    const { terminalApplicable, nonAccessibleTerminals } = this.state.holidayDetails;
    const disableCountry = this.isCountryDisabled();
    if (!disableCountry) {
      return [];
    }
    return [...new Set([...terminalApplicable, ...nonAccessibleTerminals])];
  };

  saveHoliday = () => {
    this.setState({
      isUpdateInProgress: true
    });
    const {
      countryApplicableBackupData,
      nonAccessibleTerminals,
      ...restHolidayDetails
    } = this.state.holidayDetails;
    const requestData = {
      ...restHolidayDetails,
      holidayName: sanitizeInput(restHolidayDetails.holidayName),
      countryApplicable: this.getCountryApplicableData(),
      terminalApplicable: this.getTerminalApplicableData(),
    };
    const isNewHoliday = !requestData.holidayId;
    postData('SaveHoliday', requestData).then(
      (response) => {
        if (response && response.status >= 200 && response.status < 400) {
          this.props.enqueueSnackbar(
            ` Holiday ${isNewHoliday ? 'created' : 'updated'} successfully`,
            {
              variant: 'success'
            }
          );
          this.setState({ isUpdateInProgress: false });
          this.props.onClose([response.data.holidayId]);
          this.props.fetchHolidays();
        } else {
          this.handleError({ statusCode: response.status, isNewHoliday });
        }
      },
      (_error) => {
        this.handleError({ isNewHoliday });
      }
    );
  };

  handleError = ({ statusCode, isNewHoliday }) => {
    if (statusCode !== 403) {
      this.props.enqueueSnackbar(`Error while ${isNewHoliday ? 'creating' : 'updating'} holiday`, {
        variant: 'error'
      });
    }
    this.setState({
      isUpdateInProgress: false
    });
    this.props.onClose();
  };

  getFormattedTerminalOptions = () => {
    if (this.state.holidayDetails.countryApplicable?.length > 0) {
      const countryTerminals = this.props.userAccessibleTerminals.filter((terminal) =>
        this.state.holidayDetails.countryApplicable.includes(terminal.country)
      );
      return this.getTerminalsGroupedByRegions(countryTerminals);
    }
    return this.getTerminalsGroupedByRegions(this.props.userAccessibleTerminals);
  };

  getTerminalsGroupedByRegions = (options) => {
    const groupedByRegion = groupBy(options, 'regionName');
    return Object.keys(groupedByRegion).map((region) => {
      const terminals = groupedByRegion[region].map((terminal) => ({
        terminalCode: terminal.value,
        terminalId: terminal.terminalId,
        terminalRegion: terminal.regionName
      }));

      return {
        region,
        regionTerminals: terminals
      };
    });
  };

  getButtonLabel = () => {
    return this.state.holidayDetails.holidayId ? 'Update' : 'Add';
  };

  handleRepeatYearToggle = () => {
    this.setState((prevState) => ({
      ...prevState,
      holidayDetails: {
        ...prevState.holidayDetails,
        shouldRepeatEveryYear: !prevState.holidayDetails.shouldRepeatEveryYear
      }
    }));
  };

  onTerminalChange = (selectedTerminals) => {
    this.setState((prevState) => ({
      ...prevState,
      holidayDetails: {
        ...prevState.holidayDetails,
        terminalApplicable: selectedTerminals
      }
    }));
  };

  getFilteredAccessibleTerminals = () => {
    return this.state.holidayDetails.terminalApplicable.filter((selectedTerminal) =>
      this.props.userAccessibleTerminals.find(
        (userTerminal) => userTerminal.value === selectedTerminal
      )
    );
  };

  isCountryDisabled = () => {
    return  this.state.holidayDetails?.terminalApplicable?.length > 0;
  }

  render() {
    const { classes, countryList } = this.props;
    const { holidayDetails, holidayDetailsError, showEditConfirmationDialog } = this.state;
    const holidayDate = holidayDetails.holidayDate
      ? formatMonthDateYear({ value: holidayDetails.holidayDate })
      : null;
    const disableCountry = this.isCountryDisabled();
    const filteredAccessibleTerminalOptions = this.getFilteredAccessibleTerminals();
    return (
      <>
        <div>
          <header className={classes.moduleHeader}>
            {holidayDetails.holidayId ? 'Update Holiday' : 'Add Holiday'}
          </header>
          <div className={classes.formContainer}>
            <CustomTextField
              id="HolidayName"
              label="Holiday Name"
              handleChange={this.handleChange('holidayName')}
              error={holidayDetailsError.holidayName}
              value={holidayDetails.holidayName}
              inputProps={{
                maxLength: constants.MAX_HOLIDAY_LENGTH,
                pattern: ALPHA_NUMERIC_REGEX_WITH_SPACING
              }}
              hideIncrementer
            />

            <div
              className={
                Boolean(holidayDetailsError.holidayDate)
                  ? classes.editTimeContainerError
                  : classes.editTimeContainer
              }>
              <DateTimeEditor
                dateType={appConstant.DATETYPES.MONTHDATEYEAR}
                InputLabelProps={{
                  classes: {
                    root: classes.labelProps,
                    filled: classes.filledLabel,
                    shrink: classes.shrink
                  }
                }}
                inputRootClass={classes.timeEditorInput}
                label="Holiday Date"
                onChange={this.updateHolidayDate}
                value={holidayDate}
                variant="filled"
                disableFocus
                enablePickerPortal
                disableFocusInput
                inputFocusedClass={classes.timeEditorInputFocused}
              />
              {Boolean(holidayDetailsError.holidayDate) && (
                <p
                  className={`MuiFormHelperText-root MuiFormHelperText-error ${classes.errorMessageVisible}`}>
                  {holidayDetailsError.holidayDate}
                </p>
              )}
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={holidayDetails.shouldRepeatEveryYear}
                  classes={{
                    root: classes.checkBoxStyle
                  }}
                  onChange={this.handleRepeatYearToggle}
                  name="yearRepeat"
                  color="primary"
                  icon={<CheckBoxOutlineBlankIcon className={classes.sizeIcon} />}
                  checkedIcon={<CheckBoxIcon className={classes.sizeIcon} />}
                />
              }
              label={
                <span className={classes.checkBoxLabelContainer}>
                  <RepeatEveryYear />
                  <span className={classes.checkBoxLabel}>Repeats every year</span>
                </span>
              }
            />
            <Select
              textFieldProps={{
                id: 'Country',
                label: 'Country (Optional)',
                variant: 'outlined',
                helperText: ''
              }}
              value={holidayDetails?.countryApplicable?.split(',') || null}
              error={holidayDetailsError.countryApplicable}
              onChange={this.handleChange('countryApplicable')}
              data={countryList}
              rootClass={disableCountry ? classes.inputDisabled : ''}
              isMulti
              isDisabled={disableCountry}
            />
          </div>
          <div className={classes.terminalSelectContainer}>
            <TerminalSelect
              terminalOptions={this.state.terminalOptions}
              onTerminalClick={this.onTerminalClick}
              onTerminalChange={this.onTerminalChange}
              closeTerminalMenu={this.closeTerminalMenu}
              selectedTerminals={filteredAccessibleTerminalOptions}
              selectedNonAssignedTerminals={filteredAccessibleTerminalOptions}
            />
            <ToolTip
              title={
                <span>
                  {constants.MESSAGE.TERMINAL_SELECT_CUSTOMER} <br /><br />
                  {constants.MESSAGE.TERMINAL_SELECT_GLOBAL}
                </span>
              }>
              <UrgentOutlined className={classes.terminalSelectWarning} />
            </ToolTip>
          </div>
          <div className={classes.actionContainer}>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={this.props.onClose}
              classes={{ root: classes.actionButton }}
              id="closeButton">
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              type="submit"
              onClick={this.saveHolidayData}
              disabled={this.state.isUpdateInProgress}
              classes={{
                root: classes.actionButton,
                disabled: classes.actionButtonDisabled
              }}>
              {this.state.isUpdateInProgress ? (
                <CircularProgress size={16} classes={{ root: classes.circularProgress }} />
              ) : (
                this.getButtonLabel()
              )}
            </Button>
          </div>
        </div>
        {showEditConfirmationDialog && (
          <EditHolidayConfirmationDialog
            holidayDetails={holidayDetails}
            onConfirm={this.saveHoliday}
            onCancel={this.onCloseEditConfirmationDialog}
            getTerminalsByCountryCodes={this.getTerminalsByCountryCodes}
            userAccessibleTerminals= {this.props.userAccessibleTerminals}
          />
        )}
      </>
    );
  }
}

export default compose(WithDrawer, withStyles(styles))(AddEditHoliday);
